import {gql} from '@apollo/client';
import {useCallback} from 'react';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ChatEventNode} from 'llm/chat-library/messages/MessageTypes';

import {ChatSessionQuery, ChatSessionQueryVariables} from './__generated/ChatSessionQuery';
import {
  ChatSessionSidebarQuery,
  ChatSessionSidebarQueryVariables,
} from './__generated/ChatSessionSidebarQuery';

export type ChatSessionNode = ExcludeInaccessible<ChatSessionQuery['chatSession']>;
export type ChatSessionSidebarNode = ExcludeInaccessible<ChatSessionSidebarQuery['chatSession']>;

export const CHAT_SESSION_QUERY = gql`
  query ChatSessionQuery($chatSessionId: StrictString!, $disableAllThreads: StrictBoolean = true)
  @engComponent(name: LLM)
  @skipAudit {
    chatSession(id: $chatSessionId) {
      ... on ChatSessionNode {
        id
        type
        name
        modifiedAt
        # For getProgressToolCalls
        allThreads(disable: $disableAllThreads) {
          id
          events {
            ... on LLMMessageChatEventNode {
              id
              createdAt
              role
              messageBlocks {
                ... on LLMJSONMessageBlockNode {
                  content
                }
              }
            }
          }
        }
        threads {
          id
          status
          events {
            ... on DisplayMessageChatEventNode {
              id
              createdAt
              role
              agentType
              citationGenerationMode
              agentApprovalDecision {
                category
                response
                requestEvent {
                  id
                }
              }
              agentApprovalRequest {
                category
              }
              messageBlocks {
                ... on DisplayTextBlockNode {
                  text
                  displayType
                }
                ... on DisplayFileBlockNode {
                  externalFileId
                  filename
                  mediaType
                }
              }
            }
            ... on StateUpdateChatEventNode {
              id
              createdAt
              newState
            }
            ... on RequestStateUpdateChatEventNode {
              id
              createdAt
              publicDataEnabled
            }
            ... on InterruptRequestChatEventNode {
              id
              createdAt
            }
            ... on InterruptChatEventNode {
              id
              createdAt
            }
            ... on AcknowledgementChatEventNode {
              id
              createdAt
              acknowledgedEvent {
                ... on InterruptRequestChatEventNode {
                  id
                }
              }
              resultEvent {
                ... on InterruptChatEventNode {
                  id
                  createdAt
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const CHAT_SESSION_SIDEBAR_QUERY = gql`
  query ChatSessionSidebarQuery($chatSessionId: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $chatSessionId) {
      ... on ChatSessionNode {
        id
        affectedObjects {
          externalId
          objectType
          objectName
          latestAction
          lastTouchedAt
          wasCreatedInSession
          artifactKind
          mediaType
        }
        inputFiles {
          externalId
          filename
          mediaType
        }
      }
    }
  }
`;

export interface UseChatSessionEventsResult {
  // The loaded chat events. Note that even in an error state, this may be returned with the
  // last-known values.
  events: Array<ChatEventNode>;
  isLoading: boolean;
  errorMessage: string | null;
  threadStatus: string | null;
  refetch: () => Promise<unknown>;
  chatType?: ChatType;
  // The raw query data, available when includeToolCalls is true.
  // Useful for extracting progress data from allThreads.
  chatSessionQueryData?: ChatSessionQuery;
  /**
   * The full chat session data. Available when data has loaded successfully.
   * Useful for computing progress data from allThreads when includeToolCalls is true.
   */
  chatSession?: ChatSessionNode;
  /** Canonical sidebar data, loaded independently when includeSidebarData is true. */
  chatSessionSidebar?: ChatSessionSidebarNode;
}

const POLL_INTERVAL_MS = 1000 * 30;

interface TestOverrides {
  pollIntervalMs: number;
  // No-op in production, this can be overridden in tests to capture the refetch function to invoke directly.
  registerRefetch: (refetch: () => void) => void;
}

// Values overridable by test code, should not be imported except by tests.
export const _testOverrides: TestOverrides = {
  pollIntervalMs: POLL_INTERVAL_MS,
  registerRefetch: () => {},
};

/**
 * Hook to get the most recent chat messages/events for display purposes.
 *
 * Right now, this polls for events every second from the GraphQL endpoint, but the
 * intention is to use websockets in the future.
 */
export function useChatSessionEvents({
  chatSessionId,
  onEventsUpdated,
  includeToolCalls,
  includeSidebarData = false,
}: {
  chatSessionId: string | null;
  onEventsUpdated?: (events: Array<ChatEventNode>) => void;
  includeToolCalls: boolean;
  includeSidebarData?: boolean;
}): UseChatSessionEventsResult {
  const queryResult = useSkippableBenchlingQuery<ChatSessionQuery, ChatSessionQueryVariables>(
    CHAT_SESSION_QUERY,
    {
      skip: !chatSessionId,
      variables: chatSessionId
        ? {
            chatSessionId,
            disableAllThreads: !includeToolCalls,
          }
        : undefined,
      // LLMFeedbackChatToolbar state is reset on refetches without WHEN_LOADING.
      renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
      pollInterval: _testOverrides.pollIntervalMs,
      // Always fetch from network when component mounts to avoid stale data.
      // When includeToolCalls is enabled, use no-cache so tool-call-only allThreads data does not
      // collide with the display-thread ChatThreadNode cache entries.
      fetchPolicy: includeToolCalls ? 'no-cache' : 'cache-and-network',
      onCompleted: (data) => {
        onEventsUpdated?.(
          data.chatSession.__typename === 'ChatSessionNode' ? data.chatSession.threads[0].events : []
        );
      },
    }
  );

  const sidebarQueryResult = useSkippableBenchlingQuery<
    ChatSessionSidebarQuery,
    ChatSessionSidebarQueryVariables
  >(CHAT_SESSION_SIDEBAR_QUERY, {
    skip: !chatSessionId || !includeSidebarData,
    variables: chatSessionId ? {chatSessionId} : undefined,
    pollInterval: _testOverrides.pollIntervalMs,
    fetchPolicy: 'cache-and-network',
    // Preserve the last successful sidebar data during polls and refetches so sidebarChatSession
    // does not drop to undefined while the background request is loading.
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });

  const {refetch: refetchChatSession} = queryResult;
  const {refetch: refetchSidebar} = sidebarQueryResult;
  const refetch = useCallback((): Promise<unknown> => {
    if (chatSessionId != null && includeSidebarData) {
      refetchSidebar().catch(() => undefined);
    }
    return refetchChatSession();
  }, [chatSessionId, includeSidebarData, refetchChatSession, refetchSidebar]);

  _testOverrides.registerRefetch(refetch);

  const sidebarChatSession =
    sidebarQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(sidebarQueryResult.data.chatSession)
      ? sidebarQueryResult.data.chatSession
      : undefined;

  if (queryResult.type === QueryResultType.ERROR) {
    // Even in an error state, we want to return any results we have. That way, we don't throw the
    // whole page into an error state on things like temporary network disconnects.
    const chatSession = queryResult.previousData?.chatSession;
    const chatSessionNode = chatSession != null && !isInaccessible(chatSession) ? chatSession : undefined;
    return {
      events: chatSession == null || isInaccessible(chatSession) ? [] : chatSession.threads[0].events,
      isLoading: false,
      errorMessage: 'An error occurred loading this chat. Please try again later.',
      threadStatus: chatSession == null || isInaccessible(chatSession) ? null : chatSession.threads[0].status,
      refetch,
      chatSessionQueryData: includeToolCalls ? queryResult.previousData : undefined,
      chatSession: chatSessionNode,
      chatSessionSidebar: sidebarChatSession,
    };
  } else if (queryResult.type === QueryResultType.SKIPPED) {
    return {
      events: [],
      isLoading: false,
      errorMessage: null,
      threadStatus: null,
      refetch,
      chatSessionQueryData: undefined,
      chatSession: undefined,
      chatSessionSidebar: sidebarChatSession,
    };
  } else if (queryResult.type === QueryResultType.PENDING) {
    return {
      events: [],
      isLoading: true,
      errorMessage: null,
      threadStatus: null,
      refetch,
      chatSessionQueryData: undefined,
      chatSession: undefined,
      chatSessionSidebar: sidebarChatSession,
    };
  } else {
    const {chatSession} = queryResult.data;
    if (isInaccessible(chatSession)) {
      return {
        events: [],
        isLoading: false,
        errorMessage: 'You do not have permission to access this chat.',
        threadStatus: null,
        refetch,
        chatSessionQueryData: undefined,
        chatSession: undefined,
        chatSessionSidebar: sidebarChatSession,
      };
    }
    const rootThread = chatSession.threads[0];
    return {
      events: rootThread.events,
      isLoading: false,
      errorMessage: null,
      refetch,
      chatType: chatSession.type,
      threadStatus: rootThread.status,
      chatSessionQueryData: includeToolCalls ? queryResult.data : undefined,
      chatSession,
      chatSessionSidebar: sidebarChatSession,
    };
  }
}
